// Browser-side trend helpers for the SEO Performance screen.
//
// This deliberately mirrors services/blog/performance/trendAnalysis.js rather
// than importing it: the admin is a separate Vite bundle with no path into
// firebase/functions, and shipping the Node module would drag its requires
// along. The THRESHOLDS are the contract — they must stay in step with the
// server module, so any change there belongs here too.
//
// The rule that matters most: position is INVERTED (lower is better), and
// `null` means "never ranked", which is not the same as position 0.

/** Keep in sync with POSITION_THRESHOLD in trendAnalysis.js. */
export const POSITION_THRESHOLD = 0.5;

/** Keep in sync with IMPRESSION_THRESHOLD in trendAnalysis.js. */
export const IMPRESSION_THRESHOLD = 0.15;

/** Keep in sync with MIN_IMPRESSIONS_FOR_TREND in trendAnalysis.js. */
export const MIN_IMPRESSIONS_FOR_TREND = 20;

/** Position direction — lower is better. */
export function positionDirection(before, after) {
  if (before === null && after === null) return "flat";
  if (before === null && after !== null) return "entered";
  if (before !== null && after === null) return "lost";
  const delta = after - before;
  if (Math.abs(delta) < POSITION_THRESHOLD) return "flat";
  return delta < 0 ? "improving" : "declining";
}

/** Impression direction — higher is better. */
export function impressionDirection(before, after) {
  const from = before || 0;
  const to = after || 0;
  if (from < MIN_IMPRESSIONS_FOR_TREND && to < MIN_IMPRESSIONS_FOR_TREND) return "flat";
  if (from === 0) return to > 0 ? "improving" : "flat";
  const relative = (to - from) / from;
  if (Math.abs(relative) < IMPRESSION_THRESHOLD) return "flat";
  return relative > 0 ? "improving" : "declining";
}

/** Signed delta that preserves null rather than coercing it to 0. */
export function delta(before, after) {
  if (before === null || before === undefined) return null;
  if (after === null || after === undefined) return null;
  return Math.round((after - before) * 10) / 10;
}

/** Endpoint comparison for one metric set. */
export function compare(first, last) {
  const a = first || {};
  const b = last || {};
  return {
    clicksBefore: a.clicks ?? 0,
    clicksAfter: b.clicks ?? 0,
    clicksDelta: (b.clicks ?? 0) - (a.clicks ?? 0),
    impressionsBefore: a.impressions ?? 0,
    impressionsAfter: b.impressions ?? 0,
    impressionsDelta: (b.impressions ?? 0) - (a.impressions ?? 0),
    impressionsTrend: impressionDirection(a.impressions, b.impressions),
    positionBefore: a.avgPosition ?? null,
    positionAfter: b.avgPosition ?? null,
    positionDelta: delta(a.avgPosition, b.avgPosition),
    positionTrend: positionDirection(a.avgPosition ?? null, b.avgPosition ?? null),
  };
}

/** Per-cluster trends across a chronological snapshot series. */
export function computeClusterTrends(snapshots) {
  const series = [...(snapshots || [])]
    .filter((s) => s && s.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  if (series.length < 2) {
    return { from: series[0]?.date || null, to: series[0]?.date || null, points: series.length, insufficientData: true, clusters: [] };
  }

  const first = series[0];
  const last = series[series.length - 1];
  const ids = new Set();
  series.forEach((s) => Object.keys(s.clusters || {}).forEach((id) => ids.add(id)));

  const clusters = [...ids].map((id) => ({
    id,
    ...compare(first.clusters?.[id], last.clusters?.[id]),
    history: series.map((s) => ({
      date: s.date,
      clicks: s.clusters?.[id]?.clicks ?? 0,
      impressions: s.clusters?.[id]?.impressions ?? 0,
      avgPosition: s.clusters?.[id]?.avgPosition ?? null,
    })),
  }));

  return { from: first.date, to: last.date, points: series.length, insufficientData: false, clusters };
}

/** Site-wide totals series, for the header sparklines. */
export function siteSeries(snapshots) {
  return [...(snapshots || [])]
    .filter((s) => s && s.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((s) => ({
      date: s.date,
      clicks: s.site?.clicks ?? 0,
      impressions: s.site?.impressions ?? 0,
      avgPosition: s.site?.avgPosition ?? null,
    }));
}

/** Movers only, biggest real movement first. Flat rows are dropped. */
export function notableMovers(rows, limit = 10) {
  return [...(rows || [])]
    .filter((r) => r.positionTrend !== "flat" || r.impressionsTrend !== "flat")
    .sort((a, b) => {
      const rank = (r) => (r.positionTrend === "entered" || r.positionTrend === "lost" ? 1 : 0);
      const byRank = rank(b) - rank(a);
      if (byRank !== 0) return byRank;
      return Math.abs(b.impressionsDelta || 0) - Math.abs(a.impressionsDelta || 0);
    })
    .slice(0, limit);
}

/** Tailwind colour + arrow for a trend direction. */
export function trendStyle(direction) {
  switch (direction) {
    case "improving":
    case "entered":
      return { className: "text-emerald-600", arrow: "▲" };
    case "declining":
    case "lost":
      return { className: "text-red-600", arrow: "▼" };
    default:
      return { className: "text-gray-400", arrow: "–" };
  }
}
